import React from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../Icon";
import Reveal from "../Reveal";
import Card3D from "../Card3D";
import Counter from "../Counter";
import HeroStage from "../HeroStage";
import { ABOUT_WORLDS } from "../../data/heroWorlds";
import { HERO } from "../../data/about";

/**
 * The same scene the home and services pages open on, told about the company
 * rather than the catalogue.
 *
 * `HeroStage` owns all of the motion — the phone turning towards the pointer,
 * the orbs it shoves aside, the spin-and-swap when a card is picked — and this
 * file supplies only the words and whatever closes the band. The three cards
 * on the right are this page's own chapters, so picking one previews the
 * section the reader is about to scroll into.
 *
 * The stats rail rides inside the same gradient rather than under it, so the
 * opening reads as one place instead of two dark sections stacked.
 */

function AboutHero() {
  const navigate = useNavigate();

  return (
    <HeroStage
      worlds={ABOUT_WORLDS}
      eyebrow={
        <span className="inline-flex w-fit items-center gap-2.5 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-[13px] font-semibold tracking-wide text-white backdrop-blur">
          <Icon name="sparkle" className="h-4 w-4 text-[#FBCFE8]" />
          {HERO.label}
        </span>
      }
      title={
        <>
          Everything
          <br />
          <span className="bg-[linear-gradient(100deg,#FBCFE8_0%,#E9D5FF_45%,#FFFFFF_100%)] bg-clip-text text-transparent">
            you need.
          </span>
        </>
      }
      lede={HERO.lede}
      actions={
        <>
          <button
            type="button"
            onClick={() => navigate("/contact")}
            className="group flex items-center gap-5 rounded-full bg-black/45 py-1.5 pl-6 pr-1.5 text-[15px] font-bold text-white ring-1 ring-white/15 backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:bg-black/65 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#FBCFE8]/50"
          >
            Talk to us
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FBCFE8] text-[#3B0764] transition-transform duration-500 [transition-timing-function:var(--mw-spring)] group-hover:rotate-90">
              <Icon name="arrow" className="h-[18px] w-[18px]" />
            </span>
          </button>
          <button
            type="button"
            onClick={() => navigate("/services")}
            className="rounded-full border border-white/20 px-6 py-3 text-[15px] font-semibold text-white/90 transition-colors duration-300 hover:border-white/40 hover:text-white focus:outline-none focus-visible:ring-4 focus-visible:ring-white/25"
          >
            Explore services
          </button>
        </>
      }
      footnote={
        <div className="flex items-center gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/[0.07] text-[#FBCFE8] backdrop-blur">
            <Icon name="pin" className="h-[22px] w-[22px]" />
          </span>
          <span className="flex flex-col">
            <span className="mw-eyebrow text-[#B9A0D6]">Founded in</span>
            <span className="text-[0.85rem] font-semibold text-white">
              Amman, Jordan · 2017
            </span>
          </span>
        </div>
      }
      sideTitle={
        <>
          One team,
          <br />
          <span className="bg-[linear-gradient(100deg,#FFFFFF_0%,#E9D5FF_55%,#FBCFE8_100%)] bg-clip-text text-transparent">
            one place.
          </span>
        </>
      }
    >
      <div className="mx-auto max-w-7xl px-6 pb-20 md:px-10">
        {/* --------------------------- Stats rail ---------------------------
            Each card swings up out of the page on reveal, then tilts under the
            pointer with its icon, number and label held at three different
            depths — so the turn produces parallax between them instead of
            tipping a picture.

            The glass is a child plate rather than a tone on the face: the blur
            is a grouping property and would flatten everything above it. Same
            reason the sweep clips inside its own wrapper. */}
        <Reveal>
          <div className="border-t border-white/10 pt-8">
            <span className="mw-eyebrow text-[#B9A0D6]">By the numbers</span>
          </div>
        </Reveal>

        <ul className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
          {HERO.stats.map((stat, i) => (
            <Reveal as="li" key={stat.label} delay={i * 120} className="mw-reveal-3d">
              <Card3D
                tone="plate"
                max={12}
                lift={10}
                className="h-full"
                innerClassName="p-6"
              >
                <span
                  className="pointer-events-none absolute inset-0 rounded-[inherit] border border-white/[0.14] bg-white/[0.06] shadow-[inset_0_1px_0_rgba(255,255,255,0.16),0_30px_60px_-30px_rgba(0,0,0,0.75)] backdrop-blur-[20px] transition-all duration-500 group-hover:border-[#D8B4FE]/40 group-hover:bg-white/[0.1] group-hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_40px_70px_-28px_rgba(168,85,247,0.55)]"
                  aria-hidden="true"
                />
                <span
                  className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]"
                  aria-hidden="true"
                >
                  <span className="mw-sweep absolute inset-y-0 left-0 w-1/3 bg-[linear-gradient(100deg,transparent,rgba(255,255,255,0.18),transparent)]" />
                </span>

                <span className="mw-z3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[linear-gradient(140deg,rgba(216,180,254,0.3),rgba(106,13,173,0.5))] text-[#E9D5FF] shadow-[0_14px_30px_-14px_rgba(168,85,247,0.9)] ring-1 ring-white/20 transition-all duration-500 [transition-timing-function:var(--mw-spring)] group-hover:bg-[linear-gradient(140deg,#8B2BD4,#4C087D)] group-hover:text-white group-hover:shadow-[0_22px_42px_-14px_rgba(168,85,247,1)] group-hover:ring-[#D8B4FE]/60">
                  <Icon name={stat.icon} className="h-[22px] w-[22px]" />
                </span>
                <span className="mw-z2 mt-6 block text-[2rem] font-bold leading-none tracking-tight text-white sm:text-[2.35rem]">
                  <Counter
                    value={stat.value}
                    display={stat.display}
                    suffix={stat.suffix}
                  />
                </span>
                <span className="mw-z1 mt-2.5 block text-[13px] font-medium leading-snug text-[#B9A0D6] transition-colors duration-500 group-hover:text-[#DCC9F2]">
                  {stat.label}
                </span>
              </Card3D>
            </Reveal>
          ))}
        </ul>
      </div>
    </HeroStage>
  );
}

export default AboutHero;
